import axiosInstance from '../axios/axiosInstance'
import { REACT_APP_AUTH_API_DEV } from '../utils/config'

// Hand-mirrored from dev-backend/src/core/translations/review/review.types.ts.
// Change both together.

export type ReviewGroup = 'interface' | 'email' | 'glossary' | 'word' | 'story' | 'other'
export type ReviewStatus = 'to_check' | 'approved' | 'edited' | 'flagged'
export type ItemTag = 'missing' | 'stale' | 'long' | 'attention' | 'checks' | 'reported' | 'new' | 'changed'
export type ReviewAction = 'approve' | 'edit' | 'flag' | 'reset'

export interface ReviewItem {
  id: string
  group: ReviewGroup
  core: boolean
  screen: string
  label: string
  english: string
  chinese?: string
  text: string | null
  status: ReviewStatus
  tags: ItemTag[]
  note?: string
  checks?: string[]
  by?: string
  at?: string
}

export interface LanguageProgress {
  lang: string
  live: boolean
  core: { total: number; approved: number; edited: number; flagged: number; toCheck: number; missing: number }
  groups: Record<ReviewGroup, { total: number; done: number }>
  ready: boolean
}

export interface ReportSummary {
  id: string
  kind: string
  ref_id: string
  count: number
  notes: string[]
  status: string
  last: string | null
}

export interface AuditSummary {
  at: string
  counts: Record<string, Record<string, number>> | null
  reports: { reports: number; lookups: number; perThousand: number | null; alert: boolean } | null
  overdueStories: number
}

export interface ReviewList {
  lang: string
  progress: LanguageProgress
  items: ReviewItem[]
  screens: string[]
  audit: AuditSummary | null
  reports: ReportSummary[]
  afterLaunch: { newStrings: number; changedStrings: number; openReports: number }
}

export interface WordPanelData {
  word: string
  pinyin: string
  english: string
  senses: { pinyin: string; en: string }[]
  test_level: { test: string; level: string }[]
  localizedMeaning: string | null
  meaningLanguage: string | null
}

export interface ItemContext {
  screen: string
  path?: string
  highlight?: string
  neighbours?: { key: string; english: string; text: string | null }[]
  emailPreview?: string
  word?: WordPanelData
  sentences?: { storyId: string; title: string; chinese: string; english: string | null; translation: string | null }[]
  story?: { storyId: string; title: string; englishTitle: string; level: string | null; before: string | null; after: string | null }
}

export interface ItemDetail {
  item: ReviewItem
  draft: string | null
  reports: string[]
  context: ItemContext
}

export interface EmailPreview {
  subject: string
  html: string
}

// REACT_APP_AUTH_API_DEV is the API root with /v2/ (utils/config.ts).
const BASE = `${REACT_APP_AUTH_API_DEV}translations-review`

export const getReviewOverview = async (): Promise<LanguageProgress[]> =>
  (await axiosInstance.get(`${BASE}`)).data.languages

export const getReviewList = async (lang: string): Promise<ReviewList> =>
  (await axiosInstance.get(`${BASE}/${lang}`)).data

export const getReviewItem = async (lang: string, id: string): Promise<ItemDetail> =>
  (await axiosInstance.get(`${BASE}/${lang}/item`, { params: { id } })).data

export const postReviewAction = async (
  lang: string,
  body: { id: string; action: ReviewAction; text?: string; note?: string },
): Promise<ReviewItem> => (await axiosInstance.post(`${BASE}/${lang}/action`, body)).data.item

export const getEmailPreview = async (lang: string, id: string): Promise<EmailPreview> =>
  (await axiosInstance.get(`${BASE}/${lang}/email-preview`, { params: { id } })).data
